import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import Cookies from 'js-cookie';

import SocialLoginModal from '../modal/SocialLoginModal';
import InviteRedemptionModal from '../modal/InviteRedemptionModal';
import { auth } from '../../config/firebase';
import { googleAuth, appleAuth, logout } from '../../service/socialAuth';
import { updateUserPhone } from '../../api/users/saveUsers';
import { consumeReturnTo, stashReturnTo, peekReturnTo, parseSignInDeepLink } from '../../service/returnTo';
import { parseInviteDeepLink } from '../../service/inviteDeepLink';

const NAV = [
  { label: 'About', to: '/about-us' },
  { label: 'Prayers', to: '/prayers' },
  { label: 'Classes', to: '/classes' },
  { label: 'Events', to: '/events' },
  { label: 'Membership', to: '/membership2026' },
  { label: 'Contact', to: '/contact-us' },
];

// White wordmark + white nav only work over a dark hero. Paper pages
// (reset password, thank-you, 404 is navy so it stays off this list)
// would otherwise render white-on-white until the visitor scrolls.
export const PAPER_HEADER_PATHS = ['/reset-password', '/payment-success', '/membership2026/thank-you'];

// Prefixes for pages whose URL carries a token (/pay/<token>).
const PAPER_HEADER_PREFIXES = ['/pay/'];

export function pathHasPaperHeader(pathname) {
  return (
    PAPER_HEADER_PATHS.some((p) => pathname === p) ||
    PAPER_HEADER_PREFIXES.some((p) => pathname.startsWith(p))
  );
}

export default function EditorialHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const compressed = pathHasPaperHeader(location.pathname) || scrolled;
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [signedIn, setSignedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useMotionValueEvent(scrollY, 'change', (v) => setScrolled(v > 64));

  // Follow the Firebase session, not the uid cookie. The cookie outlives
  // the session, so a stale one used to render "Account" and send members
  // to /my-account, which found no Firebase user and bounced them home.
  // This also retires a setInterval that ran every 1.5s on every page.
  useEffect(
    () =>
      auth.onAuthStateChanged((user) => {
        setSignedIn(Boolean(user));
        setAuthReady(true);
      }),
    []
  );

  // Emailed "Set up my account" lands on /invite?email=… (or ?invite=1 on
  // any page). "Login To My Account" lands on /signin?next=… (or ?signin=1).
  // Wait until Firebase has answered so a signed-in member is not flashed
  // a form they do not need.
  useEffect(() => {
    if (!authReady) return;

    const invite = parseInviteDeepLink(location.search, location.pathname);
    if (invite.open) {
      if (signedIn) return;
      setInviteEmail(invite.email);
      setInviteOpen(true);
      stashReturnTo('/my-account?section=seats');
      return;
    }

    const signIn = parseSignInDeepLink(location.search, location.pathname);
    if (signIn.open) {
      stashReturnTo(signIn.next);
      if (signedIn) {
        navigate(signIn.next, { replace: true });
        return;
      }
      setLoginOpen(true);
      return;
    }

    // Logged-out hit on /my-account stashes the destination and bounces
    // home. Open Sign in so they are not staring at the homepage.
    if (!signedIn && peekReturnTo()) setLoginOpen(true);
  }, [authReady, signedIn, location.search, location.pathname, navigate]);

  // Send the member on to whatever MyAccount bounced them away from. Only
  // ever a stashed in-app path (see service/returnTo.js), so this cannot
  // become an open redirect; with nothing stashed we stay put, which is
  // exactly the old behaviour for an ordinary sign-in.
  //
  // Call this only where sign-in is genuinely FINISHED. SocialLoginModal
  // routes a member whose phone is not yet verified into
  // PhoneVerificationModal, and that flow completes through
  // handleAccountCreated below — navigating from the social handlers before
  // then would unmount the header mid-verification and strand them.
  const goToStashedReturnPath = () => {
    const returnTo = consumeReturnTo();
    if (returnTo) navigate(returnTo);
  };

  const handleGoogle = async () => {
    try {
      const res = await googleAuth();
      if (res?.needsLink) return res;
      if (res?.uid) {
        setSignedIn(true);
        // Already verified means no phone step follows and this is the
        // completion point. An unverified member is redirected by
        // handleAccountCreated once PhoneVerificationModal finishes instead.
        if (res.phoneVerified) goToStashedReturnPath();
        return res;
      }
      return null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const handleApple = async () => {
    try {
      const res = await appleAuth();
      if (res?.needsLink) return res;
      if (res?.uid) {
        setSignedIn(true);
        if (res.phoneVerified) goToStashedReturnPath();
        return res;
      }
      return null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const handleAccountCreated = async (res) => {
    if (res?.uid) {
      Cookies.set('uid', res.uid);
      Cookies.set('email', res.email);
      if (res.customerStripeID) Cookies.set('customerStripeID', res.customerStripeID);
      if (res.phoneNumber && res.phoneVerified) {
        try {
          await updateUserPhone(res.uid, res.phoneNumber, true);
          Cookies.set('phoneVerified', 'true');
        } catch (e) {
          console.error('Failed to update phone:', e);
        }
      }
      setSignedIn(true);
      // Terminal completion point for the guest/password sign-in, for the
      // invite-code activation, and for any social sign-in that needed
      // phone verification, so the emailed destination is honoured here.
      goToStashedReturnPath();
    }
  };

  const handleSignOut = async () => {
    await logout();
    // The auth listener flips `signedIn`; nothing to set here.
    setOpen(false);
    navigate('/');
  };

  return (
    <>
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backdropFilter: compressed ? 'blur(14px) saturate(140%)' : 'none',
        WebkitBackdropFilter: compressed ? 'blur(14px) saturate(140%)' : 'none',
        background: compressed ? 'rgba(255, 255, 255, 0.82)' : 'transparent',
        borderBottom: compressed ? '1px solid rgba(36,34,88,0.12)' : '1px solid transparent',
        transition: 'background 500ms ease, border-color 500ms ease, padding 500ms ease',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr auto', md: 'auto 1fr auto' },
          alignItems: 'center',
          gap: 4,
          px: { xs: 3, md: 6 },
          py: compressed ? 1.4 : 2.4,
          transition: 'padding 500ms ease',
        }}
      >
        {/* Real KBT logo — two variants:
            • Over the hero (compressed === false) → white wordmark from kbtla.org
            • When compressed (white-blur backdrop)   → colored Torah-scroll icon
            Both are crossfaded with opacity so the swap is invisible. */}
        <Box
          component={RouterLink}
          to="/home"
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
            minWidth: 150,
            height: 44,
          }}
        >
          {/* White wordmark — shown over hero */}
          <Box
            component="img"
            src="/assets/images/kbt/kbt-logo-white.png"
            alt="KBT — Kehilat Bnei Torah"
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              height: compressed ? 28 : 44,
              width: 'auto',
              display: 'block',
              opacity: compressed ? 0 : 1,
              transition: 'height 500ms ease, opacity 350ms ease',
              filter: 'drop-shadow(0 2px 8px rgba(7,6,26,0.35))',
              pointerEvents: 'none',
            }}
          />
          {/* Colored icon — shown when compressed (over white blur) */}
          <Box
            component="img"
            src="/favicon/android-chrome-512x512.png"
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              height: 36,
              width: 'auto',
              display: 'block',
              transform: 'translateY(-50%)',
              opacity: compressed ? 1 : 0,
              transition: 'opacity 350ms ease',
              pointerEvents: 'none',
            }}
          />
        </Box>

        {/* Desktop nav */}
        <Stack
          direction="row"
          spacing={4}
          justifyContent="center"
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          {NAV.map((item) => (
            <RouterLink
              key={item.to}
              to={item.to}
              style={{
                textDecoration: 'none',
                color: compressed ? '#242258' : '#ffffff',
              }}
            >
              <Box
                className="ink-link"
                sx={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 13.5,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  textShadow: compressed ? 'none' : '0 1px 12px rgba(7,6,26,0.55)',
                  transition: 'color 300ms ease, text-shadow 300ms ease',
                }}
              >
                {item.label}
              </Box>
            </RouterLink>
          ))}
        </Stack>

        {/* Right rail */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            onClick={() => navigate('/donate-now')}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              cursor: 'pointer',
              alignItems: 'center',
              gap: 1.25,
              px: 2.5,
              py: 1.1,
              border: compressed
                ? '1.5px solid var(--navy-500)'
                : '1.5px solid rgba(255,255,255,0.7)',
              borderRadius: 999,
              fontFamily: 'var(--font-body)',
              fontWeight: 700,
              fontSize: 11.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: compressed ? 'var(--navy-500)' : '#fff',
              background: 'transparent',
              transition: 'background 350ms ease, color 350ms ease, border-color 350ms ease',
              '&:hover': {
                background: compressed ? 'var(--navy-500)' : '#fff',
                color: compressed ? '#fff' : 'var(--navy-500)',
                borderColor: compressed ? 'var(--navy-500)' : '#fff',
              },
            }}
          >
            Donate
          </Box>
          <Box
            onClick={() => (signedIn ? navigate('/my-account') : setLoginOpen(true))}
            data-testid="header-account-action"
            sx={{
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontWeight: 600,
              fontSize: 11.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: compressed ? 'var(--navy-300)' : 'rgba(255,255,255,0.78)',
              display: { xs: 'none', sm: 'inline-flex' },
              '&:hover': { color: compressed ? 'var(--navy-500)' : '#fff' },
            }}
          >
            {signedIn ? 'Account' : 'Sign in'}
          </Box>
          {signedIn && (
            <Box
              onClick={handleSignOut}
              data-testid="header-signout"
              sx={{
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
                fontSize: 11.5,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: compressed ? 'var(--navy-300)' : 'rgba(255,255,255,0.78)',
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { color: compressed ? 'var(--navy-500)' : '#fff' },
              }}
            >
              Sign out
            </Box>
          )}

          {/* Mobile menu toggle */}
          <Box
            onClick={() => setOpen((v) => !v)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexDirection: 'column',
              gap: '4px',
              cursor: 'pointer',
              padding: '6px',
            }}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  display: 'block',
                  width: 22,
                  height: 1.5,
                  background: compressed ? 'var(--navy-500)' : '#fff',
                  transform:
                    open && i === 0
                      ? 'translateY(5px) rotate(45deg)'
                      : open && i === 2
                      ? 'translateY(-6px) rotate(-45deg)'
                      : 'none',
                  opacity: open && i === 1 ? 0 : 1,
                  transition: 'transform 300ms ease, opacity 200ms ease',
                }}
              />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Mobile drawer */}
      <Box
        sx={{
          display: { xs: open ? 'block' : 'none', md: 'none' },
          background: '#fff',
          borderTop: '1px solid rgba(36,34,88,0.12)',
          px: 3,
          py: 4,
        }}
      >
        <Stack spacing={2.5}>
          {NAV.map((item) => (
            <RouterLink
              key={item.to}
              to={item.to}
              style={{ textDecoration: 'none' }}
              onClick={() => setOpen(false)}
            >
              <Box
                sx={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 28,
                  /* mobile drawer always has a white background */
                  color: 'var(--navy-500)',
                }}
              >
                {item.label}
              </Box>
            </RouterLink>
          ))}
          <Box
            onClick={() => {
              setOpen(false);
              if (signedIn) navigate('/my-account');
              else setLoginOpen(true);
            }}
            data-testid="header-account-action-mobile"
            sx={{
              mt: 1,
              cursor: 'pointer',
              alignSelf: 'flex-start',
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--navy-300)',
            }}
          >
            {signedIn ? 'Account' : 'Sign in'}
          </Box>
          {signedIn && (
            <Box
              onClick={handleSignOut}
              data-testid="header-signout-mobile"
              sx={{
                mt: 1,
                cursor: 'pointer',
                alignSelf: 'flex-start',
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--navy-300)',
              }}
            >
              Sign out
            </Box>
          )}
          <Box
            onClick={() => { setOpen(false); navigate('/donate-now'); }}
            sx={{
              mt: 2,
              cursor: 'pointer',
              alignSelf: 'flex-start',
              px: 3,
              py: 1.5,
              background: 'var(--navy-500)',
              color: '#fff',
              fontFamily: 'var(--font-body)',
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              borderRadius: 999,
            }}
          >
            Donate
          </Box>
        </Stack>
      </Box>
    </motion.header>

    {/* `onOpenInviteRedemption` is what surfaces SocialLoginModal's
        "Have an invite code? Redeem it" option — it renders only when the
        prop is passed (SocialLoginModal.js:279). This is NOT redundant
        with components/header/Header.js: that header has the same wiring
        but is unreachable (its only importer, pages/Programs.js, has no
        route in routes.js), whereas this is the header the homepage
        actually renders (pages/DashboardAppPage.js:46). Invite emails now
        land on /invite, which this header opens automatically; the Sign in
        fallback still needs this prop. Removing it strands anyone whose
        email client didn't open the deep link. */}
    <SocialLoginModal
      open={loginOpen}
      handleClose={() => setLoginOpen(false)}
      googleLogin={handleGoogle}
      appleLogin={handleApple}
      onAccountCreated={handleAccountCreated}
      requirePhoneVerification={true}
      onOpenInviteRedemption={() => setInviteOpen(true)}
    />

    {/* Invite Redemption Modal (KBT-174) — finishes with the same
        {uid, email, customerStripeID, phoneNumber, phoneVerified} shape as
        the guest/social flows, and only after its own phone-verification
        step (InviteRedemptionModal.js:152-156), so it reuses
        handleAccountCreated as-is and an activating member gets cookies, state and the stashed
        return path exactly like every other sign-in. */}
    <InviteRedemptionModal
      open={inviteOpen}
      handleClose={() => setInviteOpen(false)}
      onAccountCreated={handleAccountCreated}
      initialEmail={inviteEmail}
    />
    </>
  );
}
